import { useScript } from '@hourly/hooks/useScript';
import { useEffect } from 'react';
import styles from './SignatureView.module.css';

const DocusignSessionEndType = {
  SigningComplete: 'signing_complete',
  Cancel: 'cancel',
  Decline: 'decline',
  Exception: 'exception',
  FaxPending: 'fax_pending',
  SessionTimeout: 'session_timeout',
  TtlExpired: 'ttl_expired',
  ViewingComplete: 'viewing_complete',
} as const;
type DocusignSessionEndType = (typeof DocusignSessionEndType)[keyof typeof DocusignSessionEndType];

interface DocuSignAPI {
  signing: (options: { url: string; displayFormat: 'focused'; style?: object }) => {
    on(eventName: 'ready', callback: (event: { type: 'ready' }) => void): void;
    on(
      eventName: 'sessionEnd',
      callback: (event: { type: 'sessionEnd'; sessionEndType: DocusignSessionEndType }) => void
    ): void;
    mount: (elementId: string) => void;
  };
}
declare global {
  interface Window {
    DocuSign: {
      loadDocuSign: (docusignApiKey: string) => Promise<DocuSignAPI>;
    };
  }
}

export const SignatureStatus = {
  Completed: 'Completed',
  Cancelled: 'Cancelled',
  Pending: 'Pending',
  Error: 'Error',
} as const;
export type SignatureStatus = (typeof SignatureStatus)[keyof typeof SignatureStatus];

const docusignSigningToSignatureStatusMap: Record<DocusignSessionEndType, SignatureStatus> = {
  signing_complete: SignatureStatus.Completed,
  viewing_complete: SignatureStatus.Completed,
  cancel: SignatureStatus.Cancelled,
  decline: SignatureStatus.Cancelled,
  session_timeout: SignatureStatus.Cancelled,
  fax_pending: SignatureStatus.Pending,
  exception: SignatureStatus.Error,
  ttl_expired: SignatureStatus.Error,
};

interface SignatureViewProps {
  url: string;
  onFinish: (signatureStatus: SignatureStatus) => void;
  onError: () => void;
  onLoad?: () => void;
}

const SignatureView = ({ url, onLoad, onFinish, onError }: SignatureViewProps) => {
  const status = useScript(import.meta.env.VITE_DOCUSIGN_BUNDLE_URL, { removeOnUnmount: true });

  useEffect(() => {
    if (!url) return;

    if (status === 'ready') {
      if (typeof window.DocuSign === 'undefined') {
        console.error('DocuSign SDK is not loaded.');
        onError();
        return;
      }

      if (typeof window.DocuSign.loadDocuSign !== 'function') {
        console.error(
          'DocuSign.loadDocuSign is not available or not a function. Check DocuSign SDK version.'
        );
        onError();
        return;
      }

      window.DocuSign.loadDocuSign(import.meta.env.DOCUSIGN_INTEGRATION_KEY).then((docusign) => {
        const signingSession = docusign.signing({ url, displayFormat: 'focused' });

        if (onLoad) {
          signingSession.on('ready', onLoad);
        }

        signingSession.on('sessionEnd', (event) =>
          onFinish(docusignSigningToSignatureStatusMap[event.sessionEndType])
        );

        signingSession.mount('#signatureContainer');
      });
    }
  }, [url, onLoad, onFinish, onError, status]);

  return <div id="signatureContainer" className={styles.container} />;
};

export default SignatureView;
